import React, {useContext, useEffect, useState} from "react"
import {Button, Grid, Stack, styled} from "@mui/material"
import {Logo} from "./Logo"
import {ActionContext} from "../context/ActionContextProvider"
import NavBar from "./NavBar"
import {useAuth} from "react-oidc-context"
import KundenInfo from "./KundenInfo"
import NotificationCenter from "./NotificationCenter"
import {useLocation, useNavigate} from "react-router-dom"
import {GlobalContext} from "../../anwendungskonfigurator/context-provider/GlobalContextProvider"
import Abmeldedrawer from "./Abmeldedrawer/Abmeldedrawer"
import {ROLES} from "../AppWrapper"
import {ROOT_DIALOG_POINTER} from "../../anwendungskonfigurator/components/RootComponents/RootDialog"
import {ROOT_DRAWER_POINTER} from "../../anwendungskonfigurator/components/RootComponents/RootDrawer"
import {DataContext} from "../context/DataContextProvider"
import {WorkflowStepper} from "./WorkflowStepper"
import {WorkflowContext} from "../context/WorkflowContextProvider"
import FooterContainer from "./footer/FooterContainer"
import {openTomBackoffice} from "../../anwendungskonfigurator/misc/utils"

const Wrapper = styled(Grid)(() => ({
    height: "100dvh",
    width: "100dvw",
    position: "relative",
}))

const AppHeader = styled(Grid)(({theme}) => ({
    flexBasis: "80px",
    minHeight: "80px",
    backgroundColor: theme.APP.backgroundColor,
    padding: "0 80px",
    zIndex: 10,
    borderBottom: "1px solid #eeeeee88",
    boxShadow:
        "56px 52px 21px 0 rgba(61, 61, 61, 0.00), 36px 33px 20px 0 rgba(61, 61, 61, 0.01), 20px 19px 17px 0 rgba(61, 61, 61, 0.05), 9px 8px 12px 0 rgba(61, 61, 61, 0.09), 2px 2px 7px 0 rgba(61, 61, 61, 0.10)",
}))

const AppSidebarRight = styled(Grid)(({theme}) => ({
    flexBasis: "80px",
    minWidth: "80px",
    backgroundColor: theme.APP.backgroundColor,
    padding: "40px 0",
}))
const AppCenter = styled(Grid)(() => ({
    padding: "0 96px",
    overflowY: "auto",
}))

const AppFooter = styled(Grid)(({theme}) => ({
    flexBasis: "80px",
    minHeight: "80px",
    backgroundColor: theme.APP.backgroundColor,
}))

const generateDefaultLinkList = (setIsOpenAbmeldeDrawer) => [
    {
        id: "default-admin-tom",
        displayName: "Administration tOM",
        icon: "Gear",
        onClick: openTomBackoffice,
        role: ROLES.ADMIN,
    },
    {
        id: "default-abmelden",
        displayName: "Abmelden",
        icon: "Exitpfeil",
        onClick: () => setIsOpenAbmeldeDrawer(true),
    },
]

const clickAction = (link, triggerAction, updateData, navigate, getDataFromPointer, sendDataToBackend) => () => {
    link.action && triggerAction(link.action)
    if (link.page) {
        navigate(link.path)
    } else if (link.identifierDialog) {
        updateData(link.identifierDialog, ROOT_DIALOG_POINTER)
    } else if (link.identifierDrawer) {
        updateData(link.identifierDrawer, ROOT_DRAWER_POINTER)
    } else if (link.identifierWorkflow) {
        navigate(link.path)
    } else if (link.page.startsWith("http")) {
        globalThis.open(link.path, "_blank", "noopener,noreferrer")
    } else if (link.path.startsWith("/")) {
        globalThis.open(
            globalThis.location.origin + link.path,
            "_blank",
            "noopener,noreferrer",
        )
    }
}

const hasRole = (link, role) =>
    link.role?.length ? link.role.includes(role) : true

const getRoleFromTMP = (poc_roles) => {
    if (poc_roles.includes("admin")) {
        return "admin"
    }
    return "user"
}

export const AppControls = ({children}) => {
    const {triggerAction, sendDataToBackend} = useContext(ActionContext)
    const {updateData, getDataFromPointer} = useContext(DataContext)
    const {navigation} = useContext(GlobalContext)
    const {orderedSteps} = useContext(WorkflowContext)
    const [isAppReady, setIsAppReady] = useState(false)
    const [isOpenAbmeldeDrawer, setIsOpenAbmeldeDrawer] = useState(false)
    const auth = useAuth()
    const {tid, role, poc_roles} = auth.user?.profile ?? {}
    //TODO: PLEASE IMPLEMENT THE KEYCLOAK BROKER AND THAN DELETE THIS CODE
    const tmpRole = poc_roles ? getRoleFromTMP(poc_roles) : role
    //TODO: PLEASE IMPLEMENT THE KEYCLOAK BROKER AND THAN DELETE THIS CODE
    const showFrame = window?._env_?.SHOW_FRAME ? window?._env_?.SHOW_FRAME === "true" : true
    const onLogoutButtonClicked = () => {
        setIsOpenAbmeldeDrawer(true)
    }
    const navigate = useNavigate()
    const location = useLocation()

    const linkList = generateDefaultLinkList(setIsOpenAbmeldeDrawer)

    const links = navigation ?? []

    const showItem = (link) =>
        !link.hideInNavBar && !link.showInActionBar && hasRole(link, tmpRole)

    const navBarLinks = [
        ...links.filter(showItem).map((link) => ({
            ...link,
            onClick: clickAction(
                link,
                triggerAction,
                updateData,
                navigate,
                getDataFromPointer,
                sendDataToBackend,
            ),
        })),
        ...linkList.filter(showItem),
    ]
    const actionBarLinks = links
        .filter(
            (entry) =>
                !entry.hideInNavBar &&
                entry.showInActionBar &&
                hasRole(entry, tmpRole),
        )
        .map((link) => ({
            ...link,
            onClick: clickAction(
                link,
                triggerAction,
                updateData,
                navigate,
                getDataFromPointer,
                sendDataToBackend,
            ),
        }))

    useEffect(() => {
        triggerAction("INIT_ROUTING").then(() => setIsAppReady(true))
    }, [])

    useEffect(() => {
        return () => {
            triggerAction("RESET_CONTEXTAREAS")
        }
    }, [location.pathname])

    return showFrame ? (
        <Wrapper
            container
            direction="column"
            flexWrap="nowrap">
            <AppHeader
                container
                alignItems="center"
                flexWrap="nowrap"
                justifyContent="space-between">
                <Grid
                    container
                    spacing={4}
                    alignItems="center">
                    <Logo tid={tid} />
                </Grid>
                <KundenInfo />
                <Grid
                    container
                    spacing={4}
                    alignItems="center">
                    <Button
                        variant="tertiary"
                        onClick={onLogoutButtonClicked}
                        size="small">
                        Abmelden
                    </Button>
                </Grid>
            </AppHeader>
            {/*<NotificationCenter />*/}
            <Abmeldedrawer
                open={isOpenAbmeldeDrawer}
                setIsOpenAbmeldeDrawer={setIsOpenAbmeldeDrawer}
            />
            <Grid
                container
                flexGrow={1}
                flexWrap="nowrap"
                overflow="hidden">
                <NavBar
                    links={navBarLinks}
                    anchorDirection="left"
                    defaultOpen={true}
                />
                <AppCenter flexGrow={1}>
                    <Stack spacing={2}>
                        {orderedSteps?.length > 0 && <WorkflowStepper orderedSteps={orderedSteps} />}
                        <div>{isAppReady && children}</div>
                    </Stack>
                </AppCenter>
                <AppSidebarRight>
                    <NavBar
                        links={actionBarLinks}
                        anchorDirection="right"
                        defaultOpen={false}
                    />
                </AppSidebarRight>
            </Grid>
            <AppFooter>
                <FooterContainer />
            </AppFooter>
        </Wrapper>
    ) : (
        <Wrapper
            container
            direction="column"
            flexWrap="nowrap">
            <Grid
                container
                flexGrow={1}
                flexWrap="nowrap"
                overflow="hidden">
                <AppCenter flexGrow={1}>{isAppReady && children}</AppCenter>
            </Grid>
        </Wrapper>
    )
}
